<script setup lang="ts">
// The ledger's filter chrome: one control per declared filter, then the clauses
// actually narrowing. A tab states what it can be filtered by (`LedgerFilter`);
// this bar draws that declaration and never invents a control of its own, so a
// page that adds a filter gets its control for free.
//
// The bar is one wrapping row above a 28px table, so the controls are dense:
// they share a line while they fit and the chips take the right-hand end. The
// values arrive as a whole map and leave as a whole map (`update:values`), so
// the page that holds them owns when a change becomes a query.
import { computed } from "vue";

import type {
	LedgerFilter,
	LedgerFilterValue,
	LedgerRangeValue,
} from "./ledger";
import { activeClauses } from "./ledger";

interface Props {
	filters: LedgerFilter[];
	values: Record<string, LedgerFilterValue>;
	/** The tab the server is narrowing: the controls are read-only rather than
	 * absent, so the operator still sees what the page applied for them. */
	disabled?: boolean;
	/** A drawn area in force, named by the page. It is a clause like any other —
	 * the rows are narrowed to what the draw selected — but its value is a
	 * geometry, not an entry in `values`, so it arrives as its own statement. */
	area?: { label: string } | null;
}

const props = withDefaults(defineProps<Props>(), {
	disabled: false,
	area: null,
});

const emit = defineEmits<{
	"update:values": [values: Record<string, LedgerFilterValue>];
	clear: [];
	"clear-area": [];
}>();

const clauses = computed(() => activeClauses(props.filters, props.values));

/** The chips are the filters in force plus the drawn area, which removes itself
 * through its own event because clearing it clears the map selection too. */
const chips = computed(() => clauses.value.length > 0 || props.area !== null);

/** Replace one key and hand back the whole map. A cleared key leaves the map
 * rather than sitting in it as `null`, so nothing downstream has to tell an
 * absent filter from a null one. */
function put(key: string, value: LedgerFilterValue) {
	const next: Record<string, LedgerFilterValue> = { ...props.values };
	if (value === null) delete next[key];
	else next[key] = value;
	emit("update:values", next);
}

function textValue(filter: LedgerFilter): string {
	const value = props.values[filter.key];
	return typeof value === "string" ? value : "";
}

function choiceValue(filter: LedgerFilter): string {
	const value = props.values[filter.key];
	if (value === true) return "true";
	if (value === false) return "false";
	return "";
}

function boundValue(filter: LedgerFilter, side: "min" | "max"): string {
	const value = props.values[filter.key];
	if (typeof value !== "object" || value === null) return "";
	const bound = value[side];
	return bound === null || bound === undefined ? "" : String(bound);
}

function onChoice(filter: LedgerFilter, event: Event) {
	const choice = (event.target as HTMLSelectElement).value;
	if (choice === "") {
		put(filter.key, null);
		return;
	}
	// A boolean filter's tri-state select speaks the cell's own vocabulary; an
	// enum filter's member is the cell text itself.
	put(filter.key, filter.kind === "boolean" ? choice === "true" : choice);
}

function onText(filter: LedgerFilter, event: Event) {
	const text = (event.target as HTMLInputElement).value;
	put(filter.key, text === "" ? null : text);
}

function onBound(filter: LedgerFilter, side: "min" | "max", event: Event) {
	const raw = (event.target as HTMLInputElement).value;
	const current = props.values[filter.key];
	const range: LedgerRangeValue =
		typeof current === "object" && current !== null ? { ...current } : {};
	if (raw === "") {
		delete range[side];
	} else if (filter.kind === "number") {
		const number = Number(raw);
		if (Number.isFinite(number)) range[side] = number;
		else delete range[side];
	} else {
		range[side] = raw;
	}
	const open =
		(range.min === undefined || range.min === null) &&
		(range.max === undefined || range.max === null);
	put(filter.key, open ? null : range);
}
</script>

<template>
	<div class="filter-bar">
		<label v-for="filter in filters" :key="filter.key" class="filter-field">
			<span class="filter-label">{{ filter.label }}</span>

			<select
				v-if="filter.kind === 'enum'"
				class="form-select form-select-sm filter-enum"
				:value="textValue(filter)"
				:disabled="disabled"
				:aria-label="filter.label"
				@change="onChoice(filter, $event)"
			>
				<option value="">All</option>
				<option
					v-for="option in filter.options ?? []"
					:key="option.value"
					:value="option.value"
				>
					{{ option.label }}
				</option>
			</select>

			<select
				v-else-if="filter.kind === 'boolean'"
				class="form-select form-select-sm filter-boolean"
				:value="choiceValue(filter)"
				:disabled="disabled"
				:aria-label="filter.label"
				@change="onChoice(filter, $event)"
			>
				<option value="">Any</option>
				<option value="true">Yes</option>
				<option value="false">No</option>
			</select>

			<template v-else-if="filter.kind === 'number'">
				<input
					type="number"
					class="form-control form-control-sm filter-bound"
					placeholder="min"
					:value="boundValue(filter, 'min')"
					:disabled="disabled"
					:aria-label="`${filter.label} minimum`"
					@input="onBound(filter, 'min', $event)"
				/>
				<span class="filter-dash">–</span>
				<input
					type="number"
					class="form-control form-control-sm filter-bound"
					placeholder="max"
					:value="boundValue(filter, 'max')"
					:disabled="disabled"
					:aria-label="`${filter.label} maximum`"
					@input="onBound(filter, 'max', $event)"
				/>
			</template>

			<template v-else-if="filter.kind === 'date'">
				<input
					type="date"
					class="form-control form-control-sm filter-bound"
					:value="boundValue(filter, 'min')"
					:disabled="disabled"
					:aria-label="`${filter.label} from`"
					@input="onBound(filter, 'min', $event)"
				/>
				<span class="filter-dash">–</span>
				<input
					type="date"
					class="form-control form-control-sm filter-bound"
					:value="boundValue(filter, 'max')"
					:disabled="disabled"
					:aria-label="`${filter.label} to`"
					@input="onBound(filter, 'max', $event)"
				/>
			</template>

			<input
				v-else
				type="text"
				class="form-control form-control-sm filter-text"
				placeholder="Filter"
				:value="textValue(filter)"
				:disabled="disabled"
				:aria-label="filter.label"
				@input="onText(filter, $event)"
			/>
		</label>

		<!-- What is in force, named by the same `clauseLabel` the table's
		     narrowing comes from, so a chip cannot claim a filter the rows do
		     not have. Each removes exactly itself; the drawn area removes the
		     map selection it produced, which is why it is its own event. -->
		<div v-if="chips" class="filter-chips">
			<span v-if="area" class="badge text-bg-light filter-chip">
				{{ area.label }}
				<button
					type="button"
					class="filter-chip-remove"
					:disabled="disabled"
					:aria-label="`Clear the drawn area ${area.label}`"
					@click="emit('clear-area')"
				>
					×
				</button>
			</span>
			<span
				v-for="clause in clauses"
				:key="clause.key"
				class="badge text-bg-light filter-chip"
			>
				{{ clause.label }}
				<button
					type="button"
					class="filter-chip-remove"
					:disabled="disabled"
					:aria-label="`Remove filter ${clause.label}`"
					@click="put(clause.key, null)"
				>
					×
				</button>
			</span>
			<!-- A drawn area is its own clause and removes itself, so with no
			     filter set there is nothing for this button to clear. -->
			<button
				v-if="clauses.length > 0"
				type="button"
				class="btn btn-sm btn-outline-secondary filter-clear"
				:disabled="disabled"
				@click="emit('clear')"
			>
				Clear all
			</button>
		</div>
	</div>
</template>

<style scoped>
/* One wrapping row: controls on the left, chips pushed to the right. Nothing
   here may grow, because the table below owns the drawer's height. */
.filter-bar {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 3px 6px;
	flex: 0 0 auto;
	padding: 2px 6px;
	background: #f8f9fa;
	border-bottom: 1px solid #dee2e6;
	font-size: 0.75rem;
}

.filter-field {
	display: flex;
	align-items: center;
	gap: 3px;
	margin: 0;
	min-width: 0;
}

.filter-label {
	color: #6c757d;
	font-size: 0.7rem;
	white-space: nowrap;
}

/* Bootstrap's `-sm` sizing is a form's; the ledger's rows are 28px, so the
   controls keep the same height as a tab. */
.filter-bar .form-select,
.filter-bar .form-control {
	height: 22px;
	padding: 0 4px;
	font-size: 0.72rem;
}

/* A 22px select still has to show Bootstrap's arrow: keep it inside the box
   instead of letting the default 16px glyph sit half outside. */
.filter-bar .form-select {
	padding-right: 15px;
	background-position: right 3px center;
	background-size: 9px 7px;
}

.filter-enum {
	width: auto;
	min-width: 6.5rem;
	max-width: 11rem;
}

.filter-boolean {
	width: auto;
	min-width: 4rem;
}

.filter-text {
	width: 7.5rem;
}

.filter-bound {
	width: 5.5rem;
}

.filter-dash {
	color: #adb5bd;
}

.filter-chips {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 4px;
	margin-left: auto;
}

.filter-chip {
	display: inline-flex;
	align-items: center;
	gap: 1px;
	padding: 0 1px 0 5px;
	font-size: 0.7rem;
	font-weight: 500;
	line-height: 18px;
}

.filter-chip-remove {
	border: 0;
	background: transparent;
	color: #6c757d;
	font-size: 0.8rem;
	line-height: 1;
	padding: 0 3px;
	cursor: pointer;
}

.filter-chip-remove:hover:not(:disabled) {
	color: #212529;
}

.filter-clear {
	--bs-btn-padding-y: 0;
	--bs-btn-padding-x: 6px;
	--bs-btn-font-size: 0.7rem;
	height: 20px;
	line-height: 1;
}
</style>
